import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Analytics Context
 *
 * Provides analytics data and functionality throughout the application.
 * Fetches from enhanced API endpoints for real-time analytics data.
 *
 * @package WooAIChatbot
 */
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
/**
 * Analytics Context
 */
const AnalyticsContext = createContext(undefined);
/**
 * Default date range (last 30 days)
 */
const getDefaultDateRange = () => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    return { from, to };
};
/**
 * Get API configuration from WordPress
 */
const getApiConfig = () => {
    const globals = window.wooAiChatbot ?? window.wooAIChatbot;
    const origin = window.location.origin;
    return {
        restUrl: `${origin}/wp-json`,
        nonce: globals?.nonce || '',
    };
};
/**
 * Build date query string
 */
const buildDateQueryString = (dateRange) => {
    const params = new URLSearchParams();
    if (dateRange.from) {
        params.append('from', dateRange.from.toISOString());
    }
    if (dateRange.to) {
        params.append('to', dateRange.to.toISOString());
    }
    return params.toString();
};
/**
 * Fetch helper for analytics endpoints
 */
async function fetchAnalyticsEndpoint(endpoint, dateRange) {
    const { restUrl, nonce } = getApiConfig();
    const queryString = buildDateQueryString(dateRange);
    const url = `${restUrl}/woo-ai/v1/analytics/${endpoint}?${queryString}`;
    const response = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
        },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(error.message || `Failed to fetch ${endpoint}`);
    }
    return response.json();
}
/**
 * Fetch summary KPIs
 */
const fetchSummary = async (dateRange) => {
    const data = await fetchAnalyticsEndpoint('summary', dateRange);
    return data.kpis;
};
/**
 * Fetch conversion funnel
 */
const fetchFunnel = async (dateRange) => {
    const data = await fetchAnalyticsEndpoint('funnel', dateRange);
    const funnel = data.funnel;
    // Backend returns { stages: [...], overallConversionRate } object, not a flat array
    const funnelStageLabels = {
        message_sent: 'Messages Sent',
        product_view: 'Products Viewed',
        add_to_cart: 'Added to Cart',
        checkout: 'Checkout Started',
    };
    if (funnel && 'stages' in funnel && Array.isArray(funnel.stages)) {
        const maxCount = funnel.stages.reduce((max, s) => Math.max(max, s.count), 1);
        return funnel.stages.map(s => ({
            stage: funnelStageLabels[s.stage] ?? s.stage.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            count: s.count,
            percentage: maxCount > 0 ? Math.round((s.count / maxCount) * 100) : 0,
            dropoffRate: s.dropoffRate,
        }));
    }
    // Fallback: already flat array
    if (Array.isArray(funnel)) {
        return funnel;
    }
    return [];
};
/**
 * Fetch hourly distribution
 */
const fetchHourly = async (dateRange) => {
    const data = await fetchAnalyticsEndpoint('hourly', dateRange);
    return data.hourly || [];
};
/**
 * Fetch top queries
 */
const fetchQueries = async (dateRange) => {
    const data = await fetchAnalyticsEndpoint('queries', dateRange);
    return data.queries || [];
};
/**
 * Fetch top products
 */
const fetchProducts = async (dateRange) => {
    const data = await fetchAnalyticsEndpoint('products', dateRange);
    return data.products || [];
};
/**
 * Fetch topic performance
 */
const fetchTopics = async (dateRange) => {
    const data = await fetchAnalyticsEndpoint('topics', dateRange);
    return data.topics || [];
};
/**
 * Fetch week-over-week comparison
 */
const fetchComparison = async () => {
    const { restUrl, nonce } = getApiConfig();
    const url = `${restUrl}/woo-ai/v1/analytics/comparison`;
    try {
        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': nonce,
            },
        });
        if (!response.ok) {
            return null;
        }
        const data = await response.json();
        return data.comparison || null;
    }
    catch {
        return null;
    }
};
/**
 * Fetch conversations from WordPress REST API
 * Note: backend returns stats object, not array of conversations
 */
const fetchConversations = async (dateRange) => {
    const { restUrl, nonce } = getApiConfig();
    const queryString = buildDateQueryString(dateRange);
    const url = `${restUrl}/woo-ai/v1/analytics/conversations?${queryString}`;
    try {
        const response = await fetch(url, {
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': nonce,
            },
        });
        if (!response.ok) {
            return { stats: null, list: [] };
        }
        const data = await response.json();
        const conversations = data.conversations;
        // Backend returns stats object, not array
        if (conversations && !Array.isArray(conversations)) {
            return { stats: conversations, list: [] };
        }
        return { stats: null, list: Array.isArray(conversations) ? conversations : [] };
    }
    catch {
        // Don't let conversations failure break the entire Promise.all
        return { stats: null, list: [] };
    }
};
/**
 * Track analytics event
 */
const trackAnalyticsEvent = async (event, data) => {
    const { restUrl, nonce } = getApiConfig();
    const url = `${restUrl}/woo-ai/v1/analytics/events`;
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
        },
        body: JSON.stringify({ event, data }),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(error.message || 'Failed to track event');
    }
};
/**
 * Export analytics as CSV
 */
const exportAnalyticsCSV = async (dateRange) => {
    const { restUrl, nonce } = getApiConfig();
    const queryString = buildDateQueryString(dateRange);
    const url = `${restUrl}/woo-ai/v1/analytics/export?${queryString}`;
    const response = await fetch(url, {
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': nonce,
        },
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Network error' }));
        throw new Error(error.message || 'Failed to export analytics');
    }
    const data = await response.json();
    // Create and download CSV file
    const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const downloadUrl = URL.createObjectURL(blob);
    link.setAttribute('href', downloadUrl);
    link.setAttribute('download', data.filename || 'analytics-export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(downloadUrl);
};
/**
 * Analytics Provider Component
 */
export const AnalyticsProvider = ({ children, initialDateRange, autoLoad = true, }) => {
    const [metrics, setMetrics] = useState(null);
    const [conversations, setConversations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentDateRange, setCurrentDateRange] = useState(initialDateRange || getDefaultDateRange());
    /**
     * Refresh analytics data from all endpoints
     */
    const refresh = useCallback(async (dateRange) => {
        const range = dateRange || currentDateRange;
        setCurrentDateRange(range);
        setLoading(true);
        setError(null);
        try {
            // Fetch all data in parallel
            const [summary, funnel, hourly, topQueries, topProducts, topicPerformance, comparison, conversationsResult,] = await Promise.all([
                fetchSummary(range),
                fetchFunnel(range),
                fetchHourly(range),
                fetchQueries(range),
                fetchProducts(range),
                fetchTopics(range),
                fetchComparison(),
                fetchConversations(range),
            ]);
            // Format date range for display
            const fromStr = range.from ? range.from.toISOString().split('T')[0] : '';
            const toStr = range.to ? range.to.toISOString().split('T')[0] : '';
            setMetrics({
                summary,
                funnel,
                hourly,
                topQueries,
                topProducts,
                topicPerformance,
                comparison,
                dateRange: {
                    from: fromStr,
                    to: toStr,
                },
            });
            setConversations(conversationsResult.list);
        }
        catch (err) {
            const apiError = {
                message: err instanceof Error ? err.message : 'An unknown error occurred',
                code: 'FETCH_ERROR',
            };
            setError(apiError);
            console.error('Analytics fetch error:', err);
        }
        finally {
            setLoading(false);
        }
    }, [currentDateRange]);
    /**
     * Track analytics event
     */
    const trackEvent = useCallback(async (event, data) => {
        try {
            await trackAnalyticsEvent(event, data);
        }
        catch (err) {
            console.error('Event tracking error:', err);
            // Don't throw - tracking errors shouldn't break the UI
        }
    }, []);
    /**
     * Export analytics as CSV
     */
    const exportCSV = useCallback(async (dateRange) => {
        const range = dateRange || currentDateRange;
        try {
            await exportAnalyticsCSV(range);
        }
        catch (err) {
            console.error('Export error:', err);
            throw err;
        }
    }, [currentDateRange]);
    /**
     * Load analytics on mount if autoLoad is enabled
     */
    useEffect(() => {
        if (autoLoad) {
            refresh();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoLoad]); // Only run on mount and autoLoad change
    const value = {
        metrics,
        conversations,
        loading,
        error,
        refresh,
        trackEvent,
        exportCSV,
    };
    return (_jsx(AnalyticsContext.Provider, { value: value, children: children }));
};
/**
 * Hook to use Analytics Context
 */
export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
};
